import React, { useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Skeleton,
  Typography,
  Chip,
  Stack,
  Divider,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import { AuthContext } from "../../utils/userProvider";
import useTransactions from "../../queries/transaction/getActiveTransactions";
import useGetPromotions from "../../queries/promotion/getPromotions";
import usePendingAudits from "../../queries/audit/getPendingAudits";
import useSearchAttempts from "../../queries/SearchAttempts/getSearchAttempts";

const BRAND = "#00BAB2";

function hasTab(authState, key) {
  return authState?.tabAccess && authState.tabAccess.includes(key);
}

function bucketByMonth(items, dateField, months = 12) {
  const labels = [];
  const counts = [];
  const map = {};

  for (let i = months - 1; i >= 0; i -= 1) {
    const m = moment().subtract(i, "months");
    const key = m.format("YYYY-MM");
    labels.push(m.format("MMM YY"));
    map[key] = 0;
  }

  (items || []).forEach((item) => {
    const value = item?.[dateField];
    if (!value) return;
    const key = moment(value).format("YYYY-MM");
    if (Object.prototype.hasOwnProperty.call(map, key)) {
      map[key] += 1;
    }
  });

  Object.keys(map).forEach((key) => {
    counts.push(map[key]);
  });

  return { labels, counts };
}

function bucketByWeek(items, dateField, weeks = 8) {
  const labels = [];
  const counts = [];
  const map = {};

  for (let i = weeks - 1; i >= 0; i -= 1) {
    const start = moment().subtract(i, "weeks").startOf("isoWeek");
    const key = start.format("GGGG-[W]WW");
    labels.push(start.format("D MMM"));
    map[key] = 0;
  }

  (items || []).forEach((item) => {
    const value = item?.[dateField];
    if (!value) return;
    const key = moment(value).startOf("isoWeek").format("GGGG-[W]WW");
    if (Object.prototype.hasOwnProperty.call(map, key)) {
      map[key] += 1;
    }
  });

  Object.keys(map).forEach((key) => {
    counts.push(map[key]);
  });

  return { labels, counts };
}

function KpiCard({ title, value, subtitle, icon, color, loading, onClick, disabled }) {
  const content = (
    <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {title}
          </Typography>
          {loading ? (
            <Skeleton width={72} height={40} sx={{ mt: 0.5 }} />
          ) : (
            <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5, color }}>
              {value.toLocaleString()}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: `${color}18`,
            color,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </CardContent>
  );

  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        height: "100%",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {onClick && !disabled ? (
        <CardActionArea onClick={onClick} sx={{ height: "100%" }}>
          {content}
        </CardActionArea>
      ) : (
        content
      )}
    </Card>
  );
}

function ChartCard({ title, children, loading }) {
  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        height: "100%",
      }}
    >
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {title}
        </Typography>
        {loading ? (
          <Skeleton variant="rounded" height={260} />
        ) : (
          <Box sx={{ width: "100%", minHeight: 280 }}>{children}</Box>
        )}
      </CardContent>
    </Card>
  );
}

export default function Overview() {
  const history = useHistory();
  const { authState } = useContext(AuthContext);

  const [txRes] = useTransactions("last12months");
  const [promoRes] = useGetPromotions();
  const [auditRes] = usePendingAudits();
  const [searchRes] = useSearchAttempts();

  const transactions = txRes.data || [];
  const promotions = promoRes.data || [];
  const pendingAudits = auditRes.data || [];
  const searchAttempts = searchRes.data || [];

  const activePromotions = useMemo(
    () => promotions.filter((p) => p.active).length,
    [promotions]
  );

  const txMonthly = useMemo(
    () => bucketByMonth(transactions, "createdAt", 12),
    [transactions]
  );
  const searchWeekly = useMemo(
    () => bucketByWeek(searchAttempts, "createdAt", 8),
    [searchAttempts]
  );
  const promoMonthly = useMemo(
    () => bucketByMonth(promotions, "startDate", 12),
    [promotions]
  );

  const recentAudits = useMemo(() => {
    return [...pendingAudits]
      .sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf())
      .slice(0, 5);
  }, [pendingAudits]);

  const canTx = hasTab(authState, "transactions");
  const canPromo = hasTab(authState, "promotions");
  const canAudit = hasTab(authState, "pending-audits");

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          Overview
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Snapshot of transactions, promotions, audits, and search demand.
        </Typography>
      </Box>

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Transactions"
            value={transactions.length}
            subtitle="Last 12 months (active)"
            icon={<ReceiptOutlinedIcon />}
            color={BRAND}
            loading={txRes.loading}
            disabled={!canTx}
            onClick={() => history.push("/dashboard/transactions")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Promotions"
            value={promotions.length}
            subtitle={`${activePromotions} active this year`}
            icon={<LocalOfferOutlinedIcon />}
            color="#0d7377"
            loading={promoRes.loading}
            disabled={!canPromo}
            onClick={() => history.push("/dashboard/promotions")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Pending Audits"
            value={pendingAudits.length}
            subtitle="Awaiting review"
            icon={<RateReviewOutlinedIcon />}
            color="#c47a11"
            loading={auditRes.loading}
            disabled={!canAudit}
            onClick={() => history.push("/dashboard/pending-audits")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            title="Search Attempts"
            value={searchAttempts.length}
            subtitle="All recorded searches"
            icon={<TravelExploreOutlinedIcon />}
            color="#2f6fed"
            loading={searchRes.loading}
            onClick={() => history.push("/dashboard/searchAttempts")}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <ChartCard title="Transactions by month" loading={txRes.loading}>
            <BarChart
              height={280}
              series={[
                {
                  data: txMonthly.counts,
                  label: "Transactions",
                  color: BRAND,
                },
              ]}
              xAxis={[{ data: txMonthly.labels, scaleType: "band" }]}
              margin={{ left: 40, right: 16, top: 24, bottom: 40 }}
              slotProps={{ legend: { hidden: true } }}
            />
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              height: "100%",
            }}
          >
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                <Typography variant="subtitle1" fontWeight={700}>
                  Recent pending audits
                </Typography>
                <Chip
                  size="small"
                  label={pendingAudits.length}
                  color="primary"
                  sx={{ fontWeight: 700 }}
                />
              </Stack>
              {auditRes.loading ? (
                <Stack spacing={1.5}>
                  {[1, 2, 3].map((n) => (
                    <Skeleton key={n} height={48} />
                  ))}
                </Stack>
              ) : recentAudits.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No pending audits right now.
                </Typography>
              ) : (
                <Stack divider={<Divider flexItem />} spacing={1.25}>
                  {recentAudits.map((audit) => {
                    const actionLabel =
                      typeof audit.action === "string"
                        ? audit.action
                        : typeof audit.type === "string"
                        ? audit.type
                        : "Audit";
                    const executiveLabel =
                      (audit.executive &&
                        (audit.executive.email ||
                          audit.executive.name ||
                          audit.executive.username)) ||
                      "—";

                    return (
                      <Box
                        key={audit.id || `${audit.createdAt}-${actionLabel}`}
                      >
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {actionLabel}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {executiveLabel} ·{" "}
                          {audit.createdAt
                            ? moment(audit.createdAt).format(
                                "DD MMM YYYY, HH:mm"
                              )
                            : "—"}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard title="Search attempts by week" loading={searchRes.loading}>
            <LineChart
              height={280}
              series={[
                {
                  data: searchWeekly.counts,
                  label: "Searches",
                  color: "#2f6fed",
                  area: true,
                },
              ]}
              xAxis={[{ data: searchWeekly.labels, scaleType: "point" }]}
              margin={{ left: 40, right: 16, top: 24, bottom: 40 }}
              slotProps={{ legend: { hidden: true } }}
            />
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard title="Promotions started by month" loading={promoRes.loading}>
            <BarChart
              height={280}
              series={[
                {
                  data: promoMonthly.counts,
                  label: "Promotions",
                  color: "#0d7377",
                },
              ]}
              xAxis={[{ data: promoMonthly.labels, scaleType: "band" }]}
              margin={{ left: 40, right: 16, top: 24, bottom: 40 }}
              slotProps={{ legend: { hidden: true } }}
            />
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
}
